import { motion, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useState } from "react";
import { useFinePointerMotion } from "@/hooks/useFinePointerMotion";
import "../../styles/interactive.css";

const INTERACTIVE_SELECTOR = 'a, button, [role="button"], input, textarea, select, label, [data-cursor]';

type CursorState = {
    visible: boolean;
    hovering: boolean;
    label: string | null;
};

function CursorLayer() {
    const x = useMotionValue(-100);
    const y = useMotionValue(-100);
    const ringX = useSpring(x, { stiffness: 380, damping: 32, mass: 0.5 });
    const ringY = useSpring(y, { stiffness: 380, damping: 32, mass: 0.5 });
    const [state, setState] = useState<CursorState>({ visible: false, hovering: false, label: null });

    useEffect(() => {
        const onMove = (event: PointerEvent) => {
            if (event.pointerType !== "mouse") return;
            x.set(event.clientX);
            y.set(event.clientY);
            const target = event.target instanceof Element ? event.target : null;
            const interactive = target?.closest(INTERACTIVE_SELECTOR) ?? null;
            const label = target?.closest("[data-cursor-label]")?.getAttribute("data-cursor-label") ?? null;
            setState((prev) => {
                const hovering = interactive !== null;
                if (prev.visible && prev.hovering === hovering && prev.label === label) return prev;
                return { visible: true, hovering, label };
            });
        };
        const onLeave = () => setState((prev) => ({ ...prev, visible: false }));

        window.addEventListener("pointermove", onMove, { passive: true });
        document.documentElement.addEventListener("pointerleave", onLeave);
        return () => {
            window.removeEventListener("pointermove", onMove);
            document.documentElement.removeEventListener("pointerleave", onLeave);
        };
    }, [x, y]);

    const mode = state.label ? "is-label" : state.hovering ? "is-hover" : "";

    return (
        <div className={`cursor-layer ${state.visible ? "is-visible" : ""}`} aria-hidden="true">
            <motion.div className={`cursor-ring ${mode}`} style={{ x: ringX, y: ringY }}>
                {state.label && <span className="cursor-label">{state.label}</span>}
            </motion.div>
            <motion.div className="cursor-dot" style={{ x, y }} />
        </div>
    );
}

/** Trailing ring cursor for mouse users. The native cursor stays visible so nothing is lost for accessibility. */
export default function CustomCursor() {
    const enabled = useFinePointerMotion();
    return enabled ? <CursorLayer /> : null;
}
